import React, { useState } from "react";
import {
  FaChevronDown,
  FaFilter,
  FaSearch,
  FaSlidersH,
  FaSyncAlt,
  FaTimes,
} from "react-icons/fa";

function FilterFields({
  ui,
  seasons = [],
  updateUi,
  debouncedSearch,
  setDebouncedSearch,
}) {
  const fieldClass =
    "min-h-12 w-full rounded-2xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-900 outline-none transition focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200 dark:border-slate-700 dark:bg-slate-950 dark:text-white dark:focus:ring-indigo-900/50";

  return (
    <div className="grid gap-3 lg:grid-cols-5">
      <div className="lg:col-span-2">
        <label className="mb-1.5 block text-xs font-bold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
          Recherche
        </label>

        <div className="relative">
          <FaSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />

          <input
            type="text"
            value={debouncedSearch}
            onChange={(event) => setDebouncedSearch(event.target.value)}
            placeholder="Article, message, saison, statut..."
            className="min-h-12 w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-sm font-semibold text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200 dark:border-slate-700 dark:bg-slate-950 dark:text-white dark:focus:ring-indigo-900/50"
          />
        </div>
      </div>

      <div>
        <label className="mb-1.5 block text-xs font-bold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
          Focus
        </label>

        <select
          value={ui.focus}
          onChange={(event) => updateUi({ focus: event.target.value })}
          className={fieldClass}
        >
          <option value="all">Tous</option>
          <option value="toPay">À régler</option>
          <option value="paid">Réglés</option>
          <option value="installments">Échéances</option>
        </select>
      </div>

      <div>
        <label className="mb-1.5 block text-xs font-bold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
          Statut
        </label>

        <select
          value={ui.status}
          onChange={(event) => updateUi({ status: event.target.value })}
          className={fieldClass}
        >
          <option value="all">Tous les statuts</option>
          <option value="Non Réglé">Non réglé</option>
          <option value="En Attente">En attente</option>
          <option value="Réglé">Réglé</option>
          <option value="Partiellement Réglé">Partiellement réglé</option>
          <option value="Annulé">Annulé</option>
          <option value="Remboursé">Remboursé</option>
        </select>
      </div>

      <div>
        <label className="mb-1.5 block text-xs font-bold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
          Saison
        </label>

        <select
          value={ui.season}
          onChange={(event) => updateUi({ season: event.target.value })}
          className={fieldClass}
        >
          <option value="all">Toutes les saisons</option>

          {seasons.map((season) => (
            <option key={season} value={season}>
              {season}
            </option>
          ))}
        </select>
      </div>

      <div className="lg:col-start-5">
        <label className="mb-1.5 block text-xs font-bold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
          Tri
        </label>

        <select
          value={ui.sort}
          onChange={(event) => updateUi({ sort: event.target.value })}
          className={fieldClass}
        >
          <option value="dateDesc">Plus récents</option>
          <option value="dateAsc">Plus anciens</option>
          <option value="amountDesc">Montant décroissant</option>
          <option value="amountAsc">Montant croissant</option>
          <option value="alpha">Article A-Z</option>
        </select>
      </div>
    </div>
  );
}

export default function ReglementsToolbar({
  ui,
  seasons = [],
  updateUi,
  debouncedSearch,
  setDebouncedSearch,
  onReset,
  loading,
  onRefresh,
  totalCount = 0,
  filteredCount = 0,
}) {
  const [filtersOpen, setFiltersOpen] = useState(false);

  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="inline-flex items-center gap-2 text-sm font-bold text-slate-950 dark:text-white">
            <FaSlidersH />
            Filtres règlements
          </p>

          <p className="mt-0.5 text-xs leading-5 text-slate-500 dark:text-slate-400">
            {filteredCount} affiché{filteredCount > 1 ? "s" : ""} sur{" "}
            {totalCount} règlement{totalCount > 1 ? "s" : ""}.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-2 sm:flex sm:items-center sm:justify-end">
          <button
            type="button"
            onClick={() => setFiltersOpen((value) => !value)}
            className="inline-flex min-h-11 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 text-sm font-bold text-slate-700 shadow-sm transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-900 lg:hidden"
          >
            <FaFilter />
            Filtres
            <FaChevronDown
              className={`text-xs transition-transform ${
                filtersOpen ? "rotate-180" : ""
              }`}
            />
          </button>

          <button
            type="button"
            onClick={onRefresh}
            disabled={loading}
            className="inline-flex min-h-11 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 text-sm font-bold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-900"
          >
            <FaSyncAlt className={loading ? "animate-spin" : ""} />
            Actualiser
          </button>

          <button
            type="button"
            onClick={onReset}
            className="col-span-2 inline-flex min-h-11 items-center justify-center gap-2 rounded-2xl bg-slate-950 px-4 text-sm font-bold text-white shadow-lg shadow-slate-300/50 transition hover:-translate-y-0.5 hover:shadow-xl dark:bg-white dark:text-slate-950 dark:shadow-black/20 sm:col-span-1"
          >
            <FaTimes />
            Réinitialiser
          </button>
        </div>
      </div>

      <div className="hidden lg:block">
        <FilterFields
          ui={ui}
          seasons={seasons}
          updateUi={updateUi}
          debouncedSearch={debouncedSearch}
          setDebouncedSearch={setDebouncedSearch}
        />
      </div>

      {filtersOpen && (
        <div className="rounded-[1.5rem] border border-slate-200 bg-white p-3 shadow-lg shadow-slate-200/60 dark:border-slate-700 dark:bg-slate-950 dark:shadow-black/20 lg:hidden">
          <FilterFields
            ui={ui}
            seasons={seasons}
            updateUi={updateUi}
            debouncedSearch={debouncedSearch}
            setDebouncedSearch={setDebouncedSearch}
          />
        </div>
      )}
    </div>
  );
}